import { cn } from "../../../../lib/utils";
import { LoadingPlaceholder } from "../../../LoadingPlaceholder/LoadingPlaceholder";
import { Button } from "../../../ui/common/button";
import { Table, TableBody, TableCell, TableRow } from "../../../ui/common/table";
import {
  UNVERSIONED_ROW_KEY,
  VERSION_HISTORY_COLUMNS,
  VERSION_HISTORY_COPY
} from "../constants/versionHistory.constants";
import { VersionDetailsDraft, VersionHistoryGroup } from "../types/versionHistory.types";
import { collectVersionNames } from "../utils/versionHistory.utils";
import { VersionHistoryRow } from "./VersionHistoryRow";
import { VersionHistoryStateMessage } from "./VersionHistoryStateMessage";

interface VersionHistoryTableProps {
  versions: VersionHistoryGroup[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  expandedVersionIds: Record<string, boolean>;
  selectedRecordIdByVersion: Record<string, string>;
  editingVersionId: string | null;
  isSavingName: boolean;
  currencyCode: string;
  editingDetailsVersionId: string | null;
  isSavingDetails: boolean;
  isActivating: boolean;
  isDeleting: boolean;
  onToggleVersion: (versionId: string) => void;
  onSelectRecord: (versionId: string, recordId: string) => void;
  onStartRename: (version: VersionHistoryGroup) => void;
  onCommitRename: (value: string) => Promise<string | null>;
  onCancelRename: () => void;
  onStartEditDetails: (version: VersionHistoryGroup) => void;
  onCommitDetails: (draft: VersionDetailsDraft) => Promise<string | null>;
  onCancelEditDetails: () => void;
  onRequestDeleteDetails: (version: VersionHistoryGroup) => void;
  onGoToMapping: (version: VersionHistoryGroup) => void;
  onRequestActivate: (version: VersionHistoryGroup) => void;
  onRequestDelete: (version: VersionHistoryGroup) => void;
}

export const VersionHistoryTable = ({
  versions,
  isLoading,
  isFetching,
  isError,
  onRetry,
  expandedVersionIds,
  selectedRecordIdByVersion,
  editingVersionId,
  isSavingName,
  currencyCode,
  editingDetailsVersionId,
  isSavingDetails,
  isActivating,
  isDeleting,
  onToggleVersion,
  onSelectRecord,
  onStartRename,
  onCommitRename,
  onCancelRename,
  onStartEditDetails,
  onCommitDetails,
  onCancelEditDetails,
  onRequestDeleteDetails,
  onGoToMapping,
  onRequestActivate,
  onRequestDelete
}: VersionHistoryTableProps) => {
  const renderBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={VERSION_HISTORY_COLUMNS.length}>
            <LoadingPlaceholder />
          </TableCell>
        </TableRow>
      );
    }

    if (isError) {
      return (
        <TableRow>
          <TableCell colSpan={VERSION_HISTORY_COLUMNS.length}>
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
              <VersionHistoryStateMessage
                tone="error"
                message={VERSION_HISTORY_COPY.versionHistoryLoadError}
              />
              <Button
                type="button"
                variant="link"
                size="sm"
                className="tw-h-auto tw-p-0 tw-shrink-0"
                onClick={onRetry}
              >
                {VERSION_HISTORY_COPY.retry}
              </Button>
            </div>
          </TableCell>
        </TableRow>
      );
    }

    if (versions.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={VERSION_HISTORY_COLUMNS.length}>
            <VersionHistoryStateMessage
              tone="info"
              message={VERSION_HISTORY_COPY.noVersionHistory}
            />
          </TableCell>
        </TableRow>
      );
    }

    return versions.map(version => {
      const versionId = version.versionId ?? UNVERSIONED_ROW_KEY;
      const isEditingDetails = editingDetailsVersionId === versionId;
      return (
        <VersionHistoryRow
          key={versionId}
          version={version}
          isExpanded={!!expandedVersionIds[versionId]}
          selectedRecordId={selectedRecordIdByVersion[versionId]}
          isEditingName={editingVersionId === versionId}
          otherVersionNames={collectVersionNames(versions, version.versionId)}
          isSavingName={editingVersionId === versionId && isSavingName}
          currencyCode={currencyCode}
          isEditingDetails={isEditingDetails}
          isSavingDetails={isEditingDetails && isSavingDetails}
          isActivating={isActivating}
          isDeleting={isDeleting}
          onToggle={() => onToggleVersion(versionId)}
          onSelectRecord={recordId => onSelectRecord(versionId, recordId)}
          onStartRename={() => onStartRename(version)}
          onCommitRename={onCommitRename}
          onCancelRename={onCancelRename}
          onStartEditDetails={() => onStartEditDetails(version)}
          onCommitDetails={onCommitDetails}
          onCancelEditDetails={onCancelEditDetails}
          onRequestDeleteDetails={() => onRequestDeleteDetails(version)}
          onGoToMapping={() => onGoToMapping(version)}
          onRequestActivate={() => onRequestActivate(version)}
          onRequestDelete={() => onRequestDelete(version)}
        />
      );
    });
  };

  return (
    <Table className={cn(isFetching && !isLoading && "tw-opacity-60 tw-pointer-events-none")}>
      <TableBody>{renderBody()}</TableBody>
    </Table>
  );
};
